import { PayloadAction } from '@reduxjs/toolkit';
import {
	AppearanceOverlayConfig,
	Gender,
	IPedFaceMorphValue,
	IPedOverlayValue,
	IPedParentsValue,
} from '~/shared';
import { createSlice } from '../wrappers';

interface CharacterEditorSlice {
	tabIndex: number;
	maxTabIndex: number;
	gender: Gender;
	overlayConfigs: AppearanceOverlayConfig[];
	parentsValue: IPedParentsValue;
	faceMorphValues: IPedFaceMorphValue[];
	overlayValues: IPedOverlayValue[];
	charCreationModalVisible: boolean;
	inputActionsLocked: boolean;
}

const initialState: CharacterEditorSlice = {
	tabIndex: 0,
	maxTabIndex: 1,
	gender: Gender.MALE,
	overlayConfigs: [],
	parentsValue: {
		momIndex: 21,
		dadIndex: 1,
		faceBlend: 0,
		skinBlend: 0,
	},
	faceMorphValues: [],
	overlayValues: [],
	charCreationModalVisible: false,
	inputActionsLocked: false,
};

const characterEditorSlice = createSlice({
	name: 'characterEditor',
	initialState,
	reducers: {
		setTabIndexPrev: (state) => {
			if (state.charCreationModalVisible) {
				return;
			}

			const index = state.tabIndex;
			state.tabIndex = index > 0 ? index - 1 : index;
		},
		setTabIndexNext: (state) => {
			if (state.charCreationModalVisible) {
				return;
			}

			const index = state.tabIndex;
			const maxIndex = state.maxTabIndex;
			state.tabIndex = index < maxIndex ? index + 1 : index;
		},
		setTabIndex: (state, action) => {
			state.tabIndex = action.payload;
		},
		setMaxTabIndex: (state, action) => {
			state.maxTabIndex = action.payload;
		},
		setGender: (state, action) => {
			state.gender = action.payload;
		},
		setParentsValue: (state, action) => {
			state.parentsValue = action.payload;
		},
		setFaceMorphValues: (state, action) => {
			state.faceMorphValues = action.payload;
		},
		setFaceMorphValue: (
			state,
			action: PayloadAction<{ index: number; value: number }>,
		) => {
			const { index, value } = action.payload;
			const valueIndex = state.faceMorphValues.findIndex(
				(element) => element.index === index,
			);

			const faceMorphValues = [...state.faceMorphValues];

			if (valueIndex !== -1) {
				state.faceMorphValues = faceMorphValues.map((item, i) =>
					i === valueIndex ? { ...item, value } : item,
				);
			}
		},
		showCharacterCreationModal: (state) => {
			state.charCreationModalVisible = true;
		},
		hideCharacterCreationModal: (state) => {
			state.charCreationModalVisible = false;
		},
		setInputActionsLocked: (state, action) => {
			state.inputActionsLocked = action.payload;
		},
	},
});

export const {
	setTabIndexPrev,
	setTabIndexNext,
	setTabIndex,
	setGender,
	setParentsValue,
	setFaceMorphValues,
	setFaceMorphValue,
	setMaxTabIndex,
	setInputActionsLocked,
} = characterEditorSlice.actions;

export default characterEditorSlice.reducer;
